import { Button, Modal } from '../ui'

/**
 * THE workbench decision dialog — one flow for every action on every step:
 * a confirmation ("Create 192 access groups from the selected rows?", "Skip
 * this folder?", "Apply split for 194 rows?"), then a console-style run log
 * that streams one line per chunk as the data comes in, then a done line
 * and Close. Batch or single, create/skip/split — same box, same rhythm.
 *
 * States: `log.length === 0 && !running` = the confirm view (Cancel /
 * Confirm); `running` = log streaming, buttons disabled; `done` = the full
 * log + Close.
 */
export function DecisionRunModal({
  open,
  title,
  confirmText,
  confirmLabel = 'Confirm',
  log,
  running,
  progress,
  error,
  onConfirm,
  onClose,
}: {
  open: boolean
  title: string
  /** What is about to happen, shown on the confirm view. */
  confirmText: React.ReactNode
  confirmLabel?: string
  /** One entry per log line; lines starting with ✗ render in danger. */
  log: string[]
  running: boolean
  /** Live line shown instead of '…' while running (e.g. "working on rows
   *  6–10 of 204"). */
  progress?: string | null
  error?: string | null
  onConfirm: () => void
  onClose: () => void
}) {
  const started = log.length > 0 || running
  return (
    <Modal open={open} title={title} onClose={() => !running && onClose()}>
      <div className="flex flex-col gap-3">
        {!started && <div className="text-sm text-ostrel-ink">{confirmText}</div>}
        {started && (
          <div
            aria-live="polite"
            className="tnum flex max-h-56 flex-col gap-0.5 overflow-y-auto rounded-sm border border-ostrel-line bg-ostrel-sand/60 p-3 font-mono text-[11px] leading-relaxed text-ostrel-ink"
          >
            {log.map((line, i) => (
              <p key={i} className={line.startsWith('✗') ? 'text-ostrel-danger' : ''}>
                {line}
              </p>
            ))}
            {running && <p className="text-ostrel-ink-muted">{progress ?? '…'}</p>}
          </div>
        )}
        {error && <p className="text-[11px] text-ostrel-danger">{error}</p>}
        <div className="flex justify-end gap-2">
          {!started || running ? (
            <>
              <Button variant="ghost" size="sm" disabled={running} onClick={onClose}>
                Cancel
              </Button>
              <Button variant="navy" size="sm" disabled={running} onClick={onConfirm}>
                {running ? 'Working…' : confirmLabel}
              </Button>
            </>
          ) : (
            <Button variant="navy" size="sm" onClick={onClose}>
              Close
            </Button>
          )}
        </div>
      </div>
    </Modal>
  )
}
